import Papa from 'papaparse';
import {
  Budget,
  CategoryItem,
  DestinationItem,
  ResponsibleItem,
  Transaction,
  ContributionCalculationType,
} from '../types/budget';
import { DEFAULT_CURRENCY } from '../constants/currency';
import {
  convertNameToId,
  normalizeResponsible,
  normalizeDestination,
} from '../utils/normalization';
import i18n from '../i18n';

// Define expected headers based on the Transaction type
const EXPECTED_HEADERS = [
  'category',
  'responsible',
  'description',
  'destination',
  'amount',
] as const;

// Helper function to safely convert a value to a number
const safeParseFloat = (value: string): number => {
  // Remove any currency symbols, spaces and convert comma decimals to dots
  const cleanValue = value
    .replace(/[^0-9,.-]/g, '') // Remove any character that's not a number, comma, dot or minus
    .replace(',', '.'); // Convert comma decimal separator to dot

  const number = parseFloat(cleanValue);

  if (isNaN(number)) {
    throw new Error(`Invalid number format: ${value}`);
  }

  return number;
};

export const importCSVFile = (
  file: File,
  defaults: {
    categories: CategoryItem[];
    responsibles: ResponsibleItem[];
    destinations: DestinationItem[];
  }
): Promise<Budget> => {
  return new Promise((resolve, reject) => {
    Papa.parse<Record<(typeof EXPECTED_HEADERS)[number], string>>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim().toLowerCase(),
      complete: (results) => {
        try {
          // Check for parsing errors
          if (results.errors && results.errors.length > 0) {
            const errorMessage = results.errors
              .map((error) => `Row ${error.row}: ${error.message}`)
              .join('\n');
            throw new Error(`CSV parsing errors:\n${errorMessage}`);
          }

          // Validate headers
          const headers = Object.keys(results.data[0] || {}).map((h) => h.toLowerCase());
          const missingHeaders = EXPECTED_HEADERS.filter(
            (header) => !headers.includes(header.toLowerCase())
          );

          if (missingHeaders.length > 0) {
            throw new Error(`Invalid CSV format. Missing headers: ${missingHeaders.join(', ')}`);
          }

          // Start with default categories and responsibles
          const categories: CategoryItem[] = [];
          const responsibles: ResponsibleItem[] = [...defaults.responsibles];
          const destinations: DestinationItem[] = [...defaults.destinations];

          // Convert parsed data to Transaction objects
          const transactions: Transaction[] = results.data.map((row, index) => {
            try {
              // Create transaction object
              const transaction: Transaction = {
                id: index + 1,
                category: convertNameToId(row.category),
                responsible: normalizeResponsible(row.responsible),
                description: row.description,
                destination: normalizeDestination(row.destination),
                amount: safeParseFloat(row.amount),
              };

              // Validate required fields
              if (!transaction.category || !transaction.responsible || !transaction.destination) {
                throw new Error('Missing required fields');
              }

              // Check and add new categories
              if (transaction.category) {
                const exists = categories.some((cat) => cat.id === transaction.category);

                if (!exists) {
                  categories.push({
                    id: transaction.category,
                    name: row.category,
                  });
                }
              }

              // Check and add new responsibles
              if (transaction.responsible) {
                const exists = responsibles.some((resp) => resp.id === transaction.responsible);

                if (!exists) {
                  responsibles.push({
                    id: transaction.responsible,
                    name:
                      transaction.responsible === 'common'
                        ? i18n.t('defaults.responsibles.common')
                        : row.responsible,
                  });
                }
              }

              // Check and add new destinations
              if (transaction.destination) {
                const exists = destinations.some((dest) => dest.id === transaction.destination);

                if (!exists) {
                  destinations.push({
                    id: transaction.destination,
                    name:
                      transaction.destination === 'cash'
                        ? i18n.t('defaults.destinations.cash')
                        : row.destination,
                  });
                }
              }

              return transaction;
            } catch (error) {
              throw new Error(
                `Invalid data in row ${index + 2}: ${error instanceof Error ? error.message : 'Unknown error'}`
              );
            }
          });

          const fileName = file.name
            .replace('.csv', '')
            .split('-')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

          const budget: Budget = {
            name: fileName,
            date: new Date(),
            transactions,
            categories,
            responsibles,
            destinations,
            settings: {
              contributionCalculationType: ContributionCalculationType.INCOME_RATIO,
              currency: DEFAULT_CURRENCY,
            },
          };

          resolve(budget);
        } catch (error) {
          reject(error);
        }
      },
      error: (error) => {
        reject(new Error(`Failed to parse CSV file: ${error.message}`));
      },
    });
  });
};
